// =============================================================================
// Optim TURPE V2 — fixtures de la forme RÉELLEMENT SERVIE par le moteur relu
// (DEV-1907, DEV-1909, DEV-1910, DEV-1911, DEV-1912, DEV-1906).
//
// Les formes sont reprises des tests des six PRs du moteur, pas devinées :
// `restitution.etat` / `etat_motif` / `etat_seuil_eur`, `economie.depassements_eur`
// / `montant_servi` / `equivalent_annuel` / `regime_fiscal`, `restitution.periode`,
// `restitution.bornes.raccordement`, `restitution.controles.denivele_actuel`,
// `restitution.entrees[]`, `restitution.conduite`, `outputs.calcul`, `cycle`, et
// les réserves avec `portee` / `champ` / `valeurs`.
//
// ⚠️ Ces fixtures sont déjà au format VIEW-MODEL (ce que l'adaptateur produit),
// comme les fixtures existantes : la page et les stories les consomment sans
// passer par la façade. La correspondance avec le document brut du moteur est
// portée par `optim-turpe-v2.adapter.ts` et ses contre-tests.
//
// La dernière fixture, `optimTurpeV2ServiAncienneForme`, ne porte AUCUN de ces
// champs : c'est elle qui prouve le repli, à l'écran comme dans les tests.
// =============================================================================

import type {
    OptimTurpeV2Etude,
    OptimTurpeV2Perimetre,
} from "@modules/courtage/types/optim-turpe-v2"

/** Fenêtre de douze mois, telle que le moteur la sert après pricing. */
const PERIODE_ANNUELLE = {
    date_from: "2025-06-01",
    date_to: "2026-05-31",
    jours: 365,
    pas_prices: 52_560,
    est_annuelle: true,
}

/** Bloc de reproductibilité (§6) — les versions qui ont produit le chiffre. */
const CALCUL = {
    bareme: {
        date_demandee: null,
        date_retenue: "2026-08-01",
        provenance: "defaut_moteur",
        grille_retenue: "2026-08-01",
    },
    versions: { pricer: "0.13.4", courtage: "a1b2c3d", moteur: "2.1.0" },
    calcule_le: "2026-09-22T08:12:00.000Z",
    fenetre: { ...PERIODE_ANNUELLE, pas_attendus: 52_560 },
    pas_natif_minutes: 10,
    mode: "optimisation",
}

/** Registre des entrées d'un C4 nominal, avec une surcharge tracée. */
const ENTREES_C4 = [
    {
        champ: "structure_tarifaire.formule_tarifaire_acheminement.fta",
        valeur: "BTSUPCU4",
        unite: null,
        provenance: "enedis" as const,
        releve_le: "2026-05-20",
        surcharge: null,
        surchargeable: true,
        pese_sur_le_resultat: true,
        statut: "retenue" as const,
    },
    {
        champ: "structure_tarifaire.puissance_souscrite_soutirage.denivele",
        valeur: { HPH: 250, HCH: 250, HPB: 250, HCB: 250 },
        unite: "kVA",
        provenance: "enedis" as const,
        releve_le: "2026-05-20",
        surcharge: null,
        surchargeable: true,
        pese_sur_le_resultat: true,
        statut: "retenue" as const,
    },
    {
        champ: "compteur.segment",
        valeur: "C4",
        unite: null,
        provenance: "derive" as const,
        releve_le: null,
        surcharge: null,
        surchargeable: false,
        pese_sur_le_resultat: true,
        statut: "retenue" as const,
    },
    {
        champ: "compteur.raccordement.puissance_raccordement_soutirage",
        valeur: 250,
        unite: "kVA",
        unite_presumee: true,
        provenance: "enedis" as const,
        releve_le: "2026-05-20",
        surcharge: null,
        surchargeable: true,
        pese_sur_le_resultat: true,
        statut: "retenue" as const,
    },
    {
        // Une valeur que NOUS avons choisie faute d'être servie : elle porte la
        // réserve `entree-au-defaut`, et elle ne se cache pas (A18).
        champ: "comptage.plages_horaires.heures_creuses",
        valeur: [{ time_from: "22:00:00", time_to: "06:00:00" }],
        unite: "plages",
        provenance: "defaut_moteur" as const,
        releve_le: null,
        surcharge: null,
        surchargeable: true,
        pese_sur_le_resultat: true,
        statut: "absente" as const,
    },
    {
        // Une surcharge du client, tracée : qui, quand, et ce qu'elle a remplacé.
        champ: "optimisation.marge_securite_pct",
        valeur: 0.05,
        unite: "fraction",
        provenance: "surcharge_client" as const,
        releve_le: null,
        surcharge: {
            auteur_id: "user-0001",
            auteur_type: "utilisateur" as const,
            date: "2026-09-22",
            valeur_remplacee: 0,
            provenance_remplacee: "defaut_moteur" as const,
        },
        surchargeable: true,
        pese_sur_le_resultat: true,
        statut: "retenue" as const,
    },
]

/**
 * 1. ÉCONOMIE. Le cas nominal : nette positive, structure ferme positive, état
 *    et seuil servis, décomposition servie, équivalent annuel calculé par le
 *    moteur, raccordement appliqué.
 */
export const optimTurpeV2ServiEconomie: OptimTurpeV2Etude = {
    _id: "servi-economie",
    type: "optimturpe_etude",
    status: "SUCCESS",
    created_at: "2026-09-22T08:12:00.000Z",
    source: { idsource: "50054931846098" },
    compteur: {
        segment: "C4",
        raccordement: { puissance_raccordement_soutirage: 250 },
    },
    inputs: {
        source: { _id_source: "servi-source-c4" },
        data: { date_from: "2025-06-01", date_to: "2026-05-31" },
        optimisation: { allow_cmdps: false, marge_securite_pct: 0.05 },
    },
    actuelle: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "BTSUPCU4" },
            puissance_souscrite_soutirage: {
                denivele: { HPH: 250, HCH: 250, HPB: 250, HCB: 250 },
            },
        },
        couts: { turpe: 6120, turpeCSF: 4200, turpeCSV: 1800, turpeCMDPS: 0 },
    },
    meilleure: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "BTSUPLU4" },
            puissance_souscrite_soutirage: {
                denivele: { HPH: 160, HCH: 160, HPB: 140, HCB: 120 },
            },
        },
        couts: { turpe: 4180, turpeCSF: 2400, turpeCSV: 1780, turpeCMDPS: 0 },
    },
    pointes_mesurees_kw: { HPH: 152, HCH: 148, HPB: 131, HCB: 112 },
    economie: {
        nette_eur: 1940,
        ferme_eur: 1640,
        depassements_eur: 300,
        cmdps_estimee_eur: 0,
        relative_pct: 31.7,
        regime_fiscal: "HTVA",
        montant_servi: true,
        montant_non_servi_cause: null,
        equivalent_annuel: {
            nette_eur: 1940,
            ferme_eur: 1640,
            depassements_eur: 300,
            facteur: 1,
            cause: null,
        },
    },
    etat: "economie",
    etat_motif: null,
    etat_cause: null,
    etat_seuil_eur: 50,
    periode: PERIODE_ANNUELLE,
    qualite_donnees: {
        niveau: "fiable",
        pas_natif_minutes: 10,
        couverture_mesuree_pct: 99.4,
        couverture_hiver_mesuree_pct: 99.1,
        couverture_comblee_pct: 0.6,
        mois_entierement_absents: [],
        plus_long_trou_jours: 0,
        jours_avec_donnee: 365,
        jours_fenetre: 365,
        promesse: {
            applicable: true,
            tenue: true,
            jours_avec_donnee: 365,
            jours_fenetre: 365,
            plus_long_trou_jours: 0,
        },
    },
    entrees: ENTREES_C4,
    bornes: {
        raccordement: {
            valeur: 250,
            unite: "kVA",
            valeur_recue: 250,
            unite_recue: null,
            unite_presumee: true,
            statut: "appliquee",
            statut_motif: null,
            pointe_mesuree_max: 152,
            pointe_mesuree_unite: "kVA",
            recommandation_bornee: false,
            borne_lache: false,
        },
    },
    controles: {
        denivele_actuel: [
            {
                classe: "HCB",
                souscrite: 250,
                pointe_mesuree: 112,
                ecart_pct: 123.2,
                verdict: "tres_au_dessus_de_la_pointe",
                unite: "kVA",
            },
            {
                classe: "HPH",
                souscrite: 250,
                pointe_mesuree: 152,
                ecart_pct: 64.5,
                verdict: "coherent",
                unite: "kVA",
            },
        ],
    },
    reserves: [
        {
            code: "entree-au-defaut",
            label: "Plages d'heures creuses : Enedis ne nous transmet pas cette valeur. Nous avons utilisé 22h à 6h. Corrigez-la si votre contrat dit autre chose.",
            portee: "entree",
            champ: "comptage.plages_horaires.heures_creuses",
        },
        {
            code: "heures-creuses-non-publiees",
            label: "Enedis ne publie pas les heures creuses de ce compteur. Plages proposées : 22h à 6h.",
            portee: "entree",
            champ: "comptage.plages_horaires.heures_creuses",
            valeurs: {
                plages_proposees: [
                    { time_from: "22:00:00", time_to: "06:00:00" },
                ],
            },
        },
        {
            code: "denivele-actuel-peu-vraisemblable",
            label: "Le dénivelé actuel (250 kVA sur HCB) est très au-dessus des appels mesurés (112 kVA). Vérifiez-le : l'économie de structure en dépend.",
            portee: "recommandation",
            champ: "structure_tarifaire.puissance_souscrite_soutirage.denivele",
        },
    ],
    conduite: { mode: "optimisation" },
    calcul: CALCUL,
    cycle: {
        id_cycle: "servi-economie",
        rang: 1,
        origine: "creation",
        id_precedente: null,
        courante: true,
    },
}

/**
 * 2. DÉJÀ OPTIMALE. Nette dans la bande du seuil : l'état le dit, et le chiffre
 *    reste affiché avec son signe plutôt que remplacé par un vide.
 */
export const optimTurpeV2ServiDejaOptimale: OptimTurpeV2Etude = {
    ...optimTurpeV2ServiEconomie,
    _id: "servi-deja-optimale",
    economie: {
        nette_eur: 12,
        ferme_eur: 12,
        depassements_eur: 0,
        cmdps_estimee_eur: 0,
        relative_pct: 0.2,
        regime_fiscal: "HTVA",
        montant_servi: true,
        equivalent_annuel: {
            nette_eur: 12,
            ferme_eur: 12,
            depassements_eur: 0,
            facteur: 1,
            cause: null,
        },
    },
    etat: "deja_optimale",
    etat_motif: null,
    reserves: [],
    cycle: {
        id_cycle: "servi-deja-optimale",
        rang: 1,
        origine: "creation",
        courante: true,
    },
}

/**
 * 3. SURCOÛT. Couvrir les dépassements coûte plus cher que de payer les dépassements
 *    (A5) : le motif est servi, le montant est négatif, et il se dit surcoût.
 */
export const optimTurpeV2ServiSurcout: OptimTurpeV2Etude = {
    ...optimTurpeV2ServiEconomie,
    _id: "servi-surcout",
    economie: {
        nette_eur: -1110,
        ferme_eur: -1410,
        depassements_eur: 300,
        cmdps_estimee_eur: 0,
        relative_pct: -18.1,
        regime_fiscal: "HTVA",
        montant_servi: true,
        equivalent_annuel: {
            nette_eur: -1110,
            ferme_eur: -1410,
            depassements_eur: 300,
            facteur: 1,
            cause: null,
        },
    },
    etat: "surcout",
    etat_motif: "couverture_du_pic",
    reserves: [
        {
            code: "couverture-du-pic-plus-chere",
            label: "Couvrir les dépassements coûte plus cher que de les payer comme aujourd'hui. Le scénario avec dépassements est présenté à côté.",
            portee: "recommandation",
            valeurs: { nette_eur: -1110, depassements_eur: 300 },
        },
    ],
    cycle: {
        id_cycle: "servi-surcout",
        rang: 1,
        origine: "creation",
        courante: true,
    },
}

/**
 * 4. GAIN PAR DÉPASSEMENTS ÉVITÉS. La nette est positive mais la structure ferme
 *    est nulle ou négative : le gain vient des seuls dépassements, et c'est dit.
 */
export const optimTurpeV2ServiGainParDepassements: OptimTurpeV2Etude = {
    ...optimTurpeV2ServiEconomie,
    _id: "servi-gain-depassements",
    economie: {
        nette_eur: 2800,
        ferme_eur: -200,
        depassements_eur: 3000,
        cmdps_estimee_eur: 0,
        relative_pct: 45.8,
        regime_fiscal: "HTVA",
        montant_servi: true,
        equivalent_annuel: {
            nette_eur: 2800,
            ferme_eur: -200,
            depassements_eur: 3000,
            facteur: 1,
            cause: null,
        },
    },
    etat: "gain_par_depassements_evites",
    etat_motif: null,
    reserves: [
        {
            code: "gain-par-depassements-evites",
            label: "Le gain vient des dépassements observés sur la période. Il se confirme s'ils se reproduisent.",
            portee: "montant",
            valeurs: { nette_eur: 2800, ferme_eur: -200 },
        },
    ],
    cycle: {
        id_cycle: "servi-gain-depassements",
        rang: 1,
        origine: "creation",
        courante: true,
    },
}

/**
 * 5. MONTANT NON SERVI (A9). La puissance et le dénivelé sont servis, le montant
 *    non, avec sa cause. Tous les montants sont `null`, aucun 0.
 */
export const optimTurpeV2ServiMontantNonServi: OptimTurpeV2Etude = {
    ...optimTurpeV2ServiEconomie,
    _id: "servi-montant-non-servi",
    economie: {
        nette_eur: null,
        ferme_eur: null,
        depassements_eur: null,
        cmdps_estimee_eur: null,
        relative_pct: null,
        regime_fiscal: "HTVA",
        montant_servi: false,
        montant_non_servi_cause: "montant-non-servi-couverture",
        equivalent_annuel: {
            nette_eur: null,
            ferme_eur: null,
            depassements_eur: null,
            facteur: null,
            cause: "montant-non-servi-couverture",
        },
    },
    etat: "montant_non_servi",
    etat_motif: null,
    qualite_donnees: {
        niveau: "faible",
        pas_natif_minutes: 30,
        couverture_mesuree_pct: 41.2,
        couverture_hiver_mesuree_pct: 12.4,
        couverture_comblee_pct: 58.8,
        mois_entierement_absents: ["2025-12", "2026-01"],
        plus_long_trou_jours: 63,
        jours_avec_donnee: 150,
        jours_fenetre: 365,
        promesse: {
            applicable: true,
            tenue: false,
            jours_avec_donnee: 150,
            jours_fenetre: 365,
            plus_long_trou_jours: 63,
        },
    },
    reserves: [
        {
            code: "montant-non-servi-couverture",
            label: "Montant non servi : 41 % de la courbe de charge est mesurée sur la période. La puissance recommandée reste valable.",
            portee: "montant",
            valeurs: { couverture_mesuree_pct: 41.2, pas_natif_minutes: 30 },
        },
        {
            code: "promesse-1102-non-tenue",
            label: "Cette étude repose sur 150 jours de données sur 365, avec un trou de 63 jours. Notre engagement est d'au moins 340 jours sur 355 sans trou de plus de 10 jours.",
            portee: "montant",
            valeurs: {
                jours_avec_donnee: 150,
                jours_fenetre: 365,
                plus_long_trou_jours: 63,
            },
        },
        {
            code: "fta-sensible-au-comblement",
            label: "La formule recommandée dépend de l'énergie estimée sur les périodes sans mesure.",
            portee: "recommandation",
        },
    ],
    cycle: {
        id_cycle: "servi-montant-non-servi",
        rang: 1,
        origine: "creation",
        courante: true,
    },
}

/**
 * 6. SCÉNARIO LIBRE (A6, §5). La « meilleure » porte la structure IMPOSÉE par
 *    l'utilisateur : `conduite.mode` le dit, et l'écart à l'actuelle est servi.
 *    D2 de la série UX du 15/10/2026 : la fixture reprenait la `meilleure` de
 *    l'étude recommandée (160 / 140 / 120) ; une étude saisie réelle relue le
 *    même jour sert la saisie dans `meilleure`, identique à
 *    `inputs.optimisation.cible`.
 */
const STRUCTURE_SAISIE_SCENARIO_LIBRE = {
    formule_tarifaire_acheminement: { fta: "BTSUPLU4" },
    puissance_souscrite_soutirage: {
        denivele: { HPH: 100, HCH: 100, HPB: 100, HCB: 100 },
    },
}

export const optimTurpeV2ServiScenarioLibre: OptimTurpeV2Etude = {
    ...optimTurpeV2ServiEconomie,
    _id: "servi-scenario-libre",
    meilleure: {
        structure_tarifaire: STRUCTURE_SAISIE_SCENARIO_LIBRE,
        couts: {
            turpe: 6550,
            turpeCSF: 1960,
            turpeCSV: 1790,
            turpeCMDPS: 2800,
        },
    },
    inputs: {
        source: { _id_source: "servi-source-c4" },
        data: { date_from: "2025-06-01", date_to: "2026-05-31" },
        optimisation: {
            mode: "price_only",
            cible: {
                formule_tarifaire_acheminement: { fta: "BTSUPLU4" },
                puissance_souscrite_soutirage: {
                    denivele: { HPH: 100, HCH: 100, HPB: 100, HCB: 100 },
                },
            },
        },
    },
    economie: {
        nette_eur: -430,
        ferme_eur: -430,
        depassements_eur: 0,
        cmdps_estimee_eur: 1200,
        relative_pct: -7,
        regime_fiscal: "HTVA",
        montant_servi: true,
        equivalent_annuel: {
            nette_eur: -430,
            ferme_eur: -430,
            depassements_eur: 0,
            facteur: 1,
            cause: null,
        },
    },
    etat: "surcout",
    etat_motif: "scenario_libre",
    conduite: {
        mode: "scenario_libre",
        cible_saisie: {
            formule_tarifaire_acheminement: { fta: "BTSUPLU4" },
            puissance_souscrite_soutirage: {
                denivele: { HPH: 100, HCH: 100, HPB: 100, HCB: 100 },
            },
        },
        cible_completee: [],
        ecarts_a_l_actuelle: [
            {
                champ: "puissance_souscrite_soutirage.denivele.HPH",
                valeur_actuelle: 250,
                valeur_scenario: 100,
            },
        ],
        id_etude_recommandee: "servi-economie",
    },
    reserves: [
        {
            code: "scenario-au-dessus-du-raccordement",
            label: "Ce scénario reste sous la puissance de raccordement connue (250 kVA).",
            portee: "recommandation",
        },
    ],
    cycle: {
        id_cycle: "servi-economie",
        rang: 2,
        origine: "scenario_libre",
        id_precedente: "servi-economie",
        courante: false,
    },
}

/**
 * 7. SEGMENT HORS PÉRIMÈTRE (A16, DEV-1912). Ce n'est pas un état de l'étude :
 *    c'est un REFUS nommé, qui porte son segment, la liste des segments couverts
 *    et son levier (`aucun`, une réponse pleine).
 */
export const optimTurpeV2ServiSegmentHorsPerimetre: OptimTurpeV2Etude = {
    _id: "servi-segment-hors-perimetre",
    type: "optimturpe_etude",
    status: "FAILURE",
    created_at: "2026-09-22T08:20:00.000Z",
    source: { idsource: "50054931846099" },
    compteur: { segment: undefined },
    inputs: {
        source: { _id_source: "servi-source-c1" },
        data: { date_from: "2025-06-01", date_to: "2026-05-31" },
    },
    error: {
        code: "segment-hors-perimetre",
        message: "L'étude ne couvre pas encore les compteurs C1.",
        levier: "aucun",
        segment: "C1",
        segments_couverts: ["C2", "C4", "C5"],
    } as OptimTurpeV2Etude["error"],
    calcul: CALCUL,
}

/**
 * 8. ÉCHEC NOMMÉ AVEC LEVIER (bloquant B9). La FTA n'est pas connue : la cause
 *    le dit, le levier est `fta`, et la page garde tous ses réglages ouverts.
 */
export const optimTurpeV2ServiEchecAvecLevier: OptimTurpeV2Etude = {
    ...optimTurpeV2ServiSegmentHorsPerimetre,
    _id: "servi-echec-avec-levier",
    compteur: { segment: "C4" },
    error: {
        code: "validation-fta-manquante",
        message:
            "La formule tarifaire d'acheminement de ce compteur n'est pas connue. Saisissez-la.",
        levier: "fta",
    } as OptimTurpeV2Etude["error"],
}

/**
 * 9. ANCIENNE FORME. Une étude calculée AVANT les six PRs du moteur : aucun des
 *    champs neufs. C'est elle qui prouve le repli — l'état est déduit du montant
 *    servi et la page le dit, la décomposition est annoncée non servie, aucun
 *    équivalent annuel n'est affiché, aucun registre, aucune borne, aucun
 *    contrôle. Et nulle part un 0 à la place d'une absence.
 */
export const optimTurpeV2ServiAncienneForme: OptimTurpeV2Etude = {
    _id: "servi-ancienne-forme",
    type: "optimturpe_etude",
    status: "SUCCESS",
    created_at: "2026-06-16T09:00:00.000Z",
    source: { idsource: "50054931846097" },
    compteur: {
        segment: "C4",
        raccordement: { puissance_raccordement_soutirage: 250 },
    },
    inputs: {
        source: { _id_source: "servi-source-ancienne" },
        data: { date_from: "2025-06-01", date_to: "2026-05-31" },
        optimisation: { allow_cmdps: true, marge_securite_pct: 5 },
    },
    actuelle: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "BTSUPCU4" },
            puissance_souscrite_soutirage: {
                denivele: { HPH: 250, HCH: 250, HPB: 250, HCB: 250 },
            },
        },
        couts: { turpe: 6120, turpeCSF: 4200, turpeCSV: 1800, turpeCMDPS: 0 },
    },
    meilleure: {
        structure_tarifaire: {
            formule_tarifaire_acheminement: { fta: "BTSUPLU4" },
            puissance_souscrite_soutirage: {
                denivele: { HPH: 160, HCH: 160, HPB: 140, HCB: 120 },
            },
        },
        couts: { turpe: 4180, turpeCSF: 2400, turpeCSV: 1780, turpeCMDPS: 0 },
        variations: { variation_absolue_turpe: -1940 },
    },
    pointes_mesurees_kw: { HPH: 152, HCH: 148, HPB: 131, HCB: 112 },
    economie: { ferme_eur: 1940, cmdps_estimee_eur: 0, nette_eur: 1940 },
    qualite_donnees: { niveau: "reserve", couverture_globale_pct: 97 },
}

/** Le périmètre servi par le moteur (`GET /optimturpe/perimetre`, DEV-1912). */
export const optimTurpeV2PerimetreServi: OptimTurpeV2Perimetre = {
    version: "1.0.0",
    segments_couverts: ["C2", "C4", "C5"],
    equivalences: { P1: "C2", P3: "C4", P4: "C5" },
    domaines_tension: { C1: "HTA", C2: "HTA", C4: "BT", C5: "BT" },
    hors_perimetre: [
        {
            segment: "C1",
            cause: "segment-hors-perimetre",
            libelle: "L'étude ne couvre pas encore les compteurs C1.",
            domaine_tension: "HTA",
        },
        {
            segment: "C3",
            cause: "segment-hors-perimetre",
            libelle: "L'étude ne couvre pas encore les compteurs C3.",
            domaine_tension: "HTA",
        },
        {
            segment: "INDET",
            cause: "segment-source-indetermine",
            libelle:
                "Enedis n'a pas déterminé le segment tarifaire de ce compteur.",
            domaine_tension: null,
        },
    ],
    populations_hors_perimetre: [
        {
            cause: "producteur-pur-hors-perimetre",
            libelle:
                "Ce point n'a que de l'injection : l'étude porte sur le soutirage.",
        },
    ],
    reserves_possibles: [
        {
            code: "autoconsommation-presente",
            libelle:
                "Ce point injecte aussi de l'énergie : l'étude ne porte que sur le soutirage.",
        },
    ],
}

/**
 * 10. ABSENCE COMPLÉTABLE (L29). Les plages d'heures creuses ne sont pas
 *     connues : le refus est nommé, et l'écran offre le geste qui ouvre le champ
 *     dans « Affiner l'étude ». C'est la fixture qui prouve, en story, qu'une
 *     information absente n'est pas un cul-de-sac.
 */
export const optimTurpeV2ServiAbsenceCompletable: OptimTurpeV2Etude = {
    ...optimTurpeV2ServiEchecAvecLevier,
    _id: "servi-absence-completable",
    error: {
        code: "param-plages-heures-creuses-manquantes",
        message: "Aucune plage d'heures creuses n'a été renseignée.",
    } as OptimTurpeV2Etude["error"],
}

/**
 * 11. BORNE DE RACCORDEMENT LEVÉE (A7, L2). La levée demandée par le client est
 *     servie au statut `levee` : le registre la relit « levée par le client » et
 *     n'offre plus le geste. La contrepartie se lit dans le motif du surcoût.
 */
export const optimTurpeV2ServiBorneLevee: OptimTurpeV2Etude = {
    ...optimTurpeV2ServiEconomie,
    _id: "servi-borne-levee",
    bornes: {
        raccordement: {
            ...(optimTurpeV2ServiEconomie.bornes?.raccordement ?? {}),
            statut: "levee",
            recommandation_bornee: false,
        },
    },
    inputs: {
        ...optimTurpeV2ServiEconomie.inputs!,
        optimisation: {
            ...(optimTurpeV2ServiEconomie.inputs?.optimisation ?? {}),
            bornes: { raccordement_levee: true },
        },
    },
}

/** Index par identifiant, pour le store en mode mock et les stories. */
export const optimTurpeV2ServiEtudes: Record<string, OptimTurpeV2Etude> = {
    "servi-economie": optimTurpeV2ServiEconomie,
    "servi-deja-optimale": optimTurpeV2ServiDejaOptimale,
    "servi-surcout": optimTurpeV2ServiSurcout,
    "servi-gain-depassements": optimTurpeV2ServiGainParDepassements,
    "servi-montant-non-servi": optimTurpeV2ServiMontantNonServi,
    "servi-scenario-libre": optimTurpeV2ServiScenarioLibre,
    "servi-segment-hors-perimetre": optimTurpeV2ServiSegmentHorsPerimetre,
    "servi-echec-avec-levier": optimTurpeV2ServiEchecAvecLevier,
    "servi-ancienne-forme": optimTurpeV2ServiAncienneForme,
    "servi-absence-completable": optimTurpeV2ServiAbsenceCompletable,
    "servi-borne-levee": optimTurpeV2ServiBorneLevee,
}
